import { Link } from '@tanstack/react-router';

import { cn } from '../../../shared/lib/cn';
import { CodeIcon, DestinationsIcon, SourcesIcon } from '../../../shared/ui/icons';
import type { TUsedInKind, TUsedInRow } from '../../../entities/used-in';

const GLYPH = { source: SourcesIcon, destination: DestinationsIcon, transform: CodeIcon };

const Tag = ({ children }: { children: string }) => (
  <span className="inline-flex h-control-xs shrink-0 items-center rounded-tag bg-chip px-inset font-mono text-micro text-primary">
    {children}
  </span>
);

const stepLabel = (kind: TUsedInKind, row: TUsedInRow): string => {
  if (kind === 'source') return 'starts it';
  const step = row.node?.name ?? 'a step that is gone';
  return row.lane ? `${step} · ${row.lane}` : step;
};

interface IUsedInRowProps {
  kind: TUsedInKind;
  name: string;
  row: TUsedInRow;
}

export const UsedInRow = ({ kind, name, row }: IUsedInRowProps) => {
  const Glyph = GLYPH[kind];
  const failed = row.failuresNow ?? 0;
  const behind =
    kind === 'transform' &&
    !row.onlyInDraft &&
    row.pinnedVersion !== null &&
    row.latestVersion !== null &&
    row.pinnedVersion < row.latestVersion;

  return (
    <div className="flex items-center gap-3 border-b border-border-row px-input py-3">
      <span
        aria-hidden
        className={cn(
          'size-dot shrink-0 rounded-full',
          row.failingNow ? 'bg-danger' : 'bg-transparent',
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-data text-primary" title={row.flowName}>
          {row.flowName}
        </p>
        <p className="mt-1 flex min-w-0 flex-wrap items-center gap-2 font-mono text-meta text-muted">
          <Glyph size={12} />
          <span className="min-w-0 truncate">{stepLabel(kind, row)}</span>
          {kind === 'transform' && row.pinnedVersion !== null && (
            <Tag>{`${row.onlyInDraft ? 'draft · ' : ''}${name} v${row.pinnedVersion}`}</Tag>
          )}
          {kind !== 'transform' && row.onlyInDraft && <Tag>draft</Tag>}
          {kind !== 'transform' && !row.onlyInDraft && row.liveVersion !== null && (
            <Tag>{`v${row.liveVersion}`}</Tag>
          )}
        </p>
        {behind && (
          <p className="mt-1 font-mono text-meta text-muted">
            v{row.latestVersion} is out — this one still runs v{row.pinnedVersion}
          </p>
        )}
      </div>
      <div className="shrink-0 text-right font-mono text-data">
        {row.onlyInDraft ? (
          <span className="text-faint" title="A draft sees no traffic">
            —
          </span>
        ) : (
          <>
            <span className="block text-primary">{row.traffic24h.toLocaleString('en-US')}</span>
            {failed > 0 && (
              <span className="block text-meta text-danger-text">
                {failed.toLocaleString('en-US')} not delivered
              </span>
            )}
          </>
        )}
      </div>
      <Link
        to="/flows/$flowId"
        params={{ flowId: row.flowId }}
        search={kind === 'source' || !row.node ? {} : { node: row.node.key }}
        className="shrink-0 text-meta text-primary underline"
      >
        Open on canvas
      </Link>
    </div>
  );
};
